import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch (err) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicRoute = path.startsWith('/werken-bij') || path.startsWith('/portaal');
    (async () => {
      if (!isPublicRoute) await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.warn('[auth] refreshMe failed', e);
    }
    setUser(null);
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, []);

  const hasRole = useCallback((...roles) => user && roles.includes(user.role), [user]);

  const canAccessLocation = useCallback(
    (slug) => {
      if (!user) return false;
      if (user.role === 'owner' || user.role === 'admin') return true;
      return (user.locations || []).includes(slug);
    },
    [user]
  );

  const value = { user, loading, login, logout, refreshMe, hasRole, canAccessLocation };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
